import React from 'react'
import Card from 'react-bootstrap/Card'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SearchableColumnSelect from './searchable-column-select'
import { DATE_FORMAT_DEFAULT } from '../helpers/chart-formatters'
import {
  faChartBar,
  faChartLine,
  faChartPie,
  faChartArea,
  faTable,
  faBraille,
  faCheckCircle,
  faHashtag,
  faFont,
  faHeading,
  faCut,
  faDatabase,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons'

/**
 * The View a report chart pulls from, at the top of the chart options modal.
 *
 * Read-only by default: linked when we can resolve where the View lives, so a filter can be
 * corrected at the source without losing the report — the link opens a new tab, because the
 * report, with this modal open over it, is what the user comes back to. `href` is empty for a
 * View whose section we can't resolve, and a removed source is never linked; both fall back to
 * plain text rather than a dead link.
 *
 * Given `options` and `onChange` it becomes a picker instead, which is the only way to re-bind a
 * chart whose data source was removed from the report and re-linked (re-linking creates a new
 * dataset row, so the old binding is gone for good — see ReportDataset's dependent: :nullify).
 */
export const DataSourceOption = ({
  Form,
  label,
  href = '',
  missing = false,
  options = [],
  value = '',
  onChange = null,
  helpText = null
}) => {
  const editable = !!onChange && options.length > 0
  if (!label && !editable) return null

  const linkable = !!href && !missing

  return (
    <div className="mb-3">
      <Form.Label className="mb-1">Data source</Form.Label>
      {editable ? (
        <>
          <Form.Select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            aria-label="Data source"
            className={missing && !value ? 'border-warning' : undefined}
          >
            <option value="">Select a data source…</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          {(helpText || (missing && !value)) && (
            <Form.Text className={missing && !value ? 'text-warning-emphasis' : 'text-muted'}>
              {helpText ||
                'This component\u2019s data source was removed from the report. Pick one to reconnect it.'}
            </Form.Text>
          )}
          {linkable && (
            <div className="mt-1">
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="d-inline-flex align-items-center gap-2 text-decoration-none small"
                title="Open this data source in a new tab"
              >
                <span>{label}</span>
                <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.75rem' }} />
              </a>
            </div>
          )}
        </>
      ) : (
        <div
          className={`border rounded px-3 py-2 d-flex align-items-center gap-2 ${
            missing ? 'border-warning bg-warning-subtle text-warning-emphasis' : 'bg-light'
          }`}
          style={{ fontSize: '0.9rem' }}
        >
          <FontAwesomeIcon icon={faDatabase} className={missing ? 'text-warning' : 'text-muted'} />
          {linkable ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="d-flex align-items-center gap-2 text-decoration-none"
              title="Open this data source in a new tab"
            >
              <span>{label}</span>
              <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.75rem' }} />
            </a>
          ) : (
            <span className={missing ? '' : 'text-muted'}>{label}</span>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Shared chart option components for reuse across different chart types.
 * Each component is a reusable Form.Group that can be used in chart renderOptions.
 */

// Chart type configuration
export const CHART_TYPES = [
  { id: 'line', label: 'Line', icon: faChartLine },
  { id: 'vertical-bar', label: 'Vertical Bar', icon: faChartBar },
  { id: 'horizontal-bar', label: 'Horizontal Bar', icon: faChartBar, iconRotation: 90 },
  { id: 'pie-donut', label: 'Pie/Donut', icon: faChartPie },
  { id: 'stacked-area', label: 'Stacked Area', icon: faChartArea },
  { id: 'scatter-plot', label: 'Scatter', icon: faBraille },
  { id: 'pivot-table', label: 'Pivot Table', icon: faTable },
  { id: 'kpi', label: 'KPI', icon: faHashtag },
  { id: 'data-table', label: 'Data Table', icon: faTable },
  { id: 'text', label: 'Text', icon: faFont },
  { id: 'header', label: 'Header', icon: faHeading },
  { id: 'page-break', label: 'Page Break', icon: faCut }
]

// Reusable chart type card component
export const ChartTypeCard = ({ type, isSelected, isDisabled, onClick }) => {
  return (
    <div className="col-3">
      <Card
        className="shadow-sm h-100"
        style={{
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          background: isSelected ? '#f0f5f7' : '',
          border: isSelected ? '1px solid #6ea0b5' : '1px solid #dee2e6',
          opacity: isDisabled && !isSelected ? 0.5 : 1
        }}
        onClick={isDisabled ? undefined : onClick}
      >
        <Card.Body className="p-2 text-center d-flex flex-column justify-content-center">
          {isSelected && (
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="text-primary position-absolute"
              style={{ top: '5px', right: '5px', fontSize: '0.8rem' }}
            />
          )}
          <FontAwesomeIcon
            icon={type.icon}
            className="mb-1"
            rotation={type.iconRotation}
          />
          <div className="small">{type.label}</div>
        </Card.Body>
      </Card>
    </div>
  )
}

export const ChartTypeOption = ({
  Form,
  chartType,
  chart,
  handleChartTypeChange
}) => {
  // Chart type cannot be changed after saving, so hide the selector entirely
  // for existing charts.
  if (chart?.id) return null

  return (
    <Form.Group className="mb-4">
      <Form.Label className="mb-3">Chart Type</Form.Label>
      <div className="row g-2">
        {CHART_TYPES.map((type) => (
          <ChartTypeCard
            key={type.id}
            type={type}
            isSelected={chartType === type.id}
            onClick={() => handleChartTypeChange(type.id)}
          />
        ))}
      </div>
    </Form.Group>
  )
}

export const ChartTitleOption = ({
  Form,
  title,
  setTitle,
  validationErrors,
  setValidationErrors,
  hasSubmitted
}) => (
  <Form.Group className="mb-3">
    <Form.Label>Title</Form.Label>
    <Form.Control
      size="sm"
      value={title}
      onChange={(e) => {
        setTitle(e.target.value)
        if (validationErrors.title) {
          setValidationErrors((prev) => ({ ...prev, title: undefined }))
        }
      }}
      isInvalid={hasSubmitted && !!validationErrors.title}
    />
    <Form.Control.Feedback type="invalid">
      {validationErrors.title}
    </Form.Control.Feedback>
  </Form.Group>
)

export const XAxisColumnOption = ({
  Form,
  xAxisColumn,
  setXAxisColumn,
  validationErrors,
  setValidationErrors,
  hasSubmitted,
  getGroupingColumns,
  getColumnDisplayName,
  municipality,
  filterByDate = false
}) => {
  const xAxisColumns = filterByDate
    ? getGroupingColumns()?.filter((c) => c.display_as === 'date')
    : getGroupingColumns()
  const isInvalid = hasSubmitted && !!validationErrors.xAxisColumn
  return (
    <Form.Group className="mb-3">
      <Form.Label>X-Axis Column</Form.Label>
      <SearchableColumnSelect
        inputId="x-axis-column-select"
        columns={xAxisColumns}
        value={xAxisColumn}
        onChange={(val) => {
          setXAxisColumn(val)
          if (validationErrors.xAxisColumn) {
            setValidationErrors((prev) => ({
              ...prev,
              xAxisColumn: undefined
            }))
          }
        }}
        getColumnDisplayName={getColumnDisplayName}
        municipality={municipality}
        isInvalid={isInvalid}
      />
      {isInvalid && (
        <div className="invalid-feedback d-block">
          {validationErrors.xAxisColumn}
        </div>
      )}
    </Form.Group>
  )
}

export const DateGroupingOption = ({
  Form,
  xAxisColumn,
  xAxisDateGrouping,
  setXAxisDateGrouping,
  columns
}) => {
  const shouldShow = xAxisColumn && columns?.find((c) => c.id === xAxisColumn)?.display_as === 'date'

  if (!shouldShow) return null

  return (
    <Form.Group className="mb-3">
      <Form.Label>Date Grouping</Form.Label>
      <Form.Select
        size="sm"
        value={xAxisDateGrouping}
        onChange={(e) => setXAxisDateGrouping(e.target.value)}
      >
        <option value="day">Day</option>
        <option value="week">Week</option>
        <option value="month">Month</option>
        <option value="year">Year</option>
      </Form.Select>
    </Form.Group>
  )
}

/**
 * The three ways an account segment can read in a table: its description, its code, or
 * both. An account segment is stored as a code ("11") plus a description ("General
 * Fund"), and charts have always shown the description alone — which drops the code a
 * finance team reads accounts by, and merges two segments that share a description.
 *
 * The option labels are shaped like the value they produce, the same way the decimals
 * options are worked examples: "Description (Code)" renders "General Fund (11)".
 */
export const SEGMENT_DISPLAY_OPTIONS = [
  { value: 'name', label: 'Description' },
  { value: 'code', label: 'Code' },
  { value: 'both', label: 'Description (Code)' }
]

export const SEGMENT_DISPLAY_DEFAULT = 'name'

/**
 * Per-column choice of segment format, used by the pivot table's row groupings and the
 * data table's display columns. Callers decide which of their columns get the control —
 * it only means anything for a column with a code and a description to choose between:
 * the raw account segments and the rollups built over them (see
 * hasSegmentDisplayChoice).
 *
 * `block` renders it as a full-width form group, for the pivot table's primary Group By
 * row; the default compact layout sits inline beside a column select.
 */
export const SegmentDisplayOption = ({
  Form,
  inputId,
  segmentDisplay,
  setSegmentDisplay,
  label = 'Show as',
  block = false,
  width = '170px'
}) => {
  const select = (
    <Form.Select
      id={inputId}
      size="sm"
      value={segmentDisplay || SEGMENT_DISPLAY_DEFAULT}
      onChange={(e) => setSegmentDisplay(e.target.value)}
    >
      {SEGMENT_DISPLAY_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Form.Select>
  )

  if (block) {
    return (
      <Form.Group className="mb-3">
        <Form.Label htmlFor={inputId}>{label}</Form.Label>
        {select}
      </Form.Group>
    )
  }

  return (
    <div style={{ width }}>
      <Form.Label className="small mb-1" htmlFor={inputId}>
        {label}
      </Form.Label>
      {select}
    </div>
  )
}

/**
 * The date formats a table column can be read in. Dates have always rendered one way —
 * "July 30, 2026" — which is right for a row label and wasteful for a column of them: a
 * data table of twelve dates spends most of its width spelling out month names.
 *
 * The option labels are the same date written each way, the same worked-example shape the
 * decimals and segment options use — "07/30/2026" says what the cell will look like more
 * directly than "Numeric" does.
 */
export const DATE_FORMAT_OPTIONS = [
  { value: 'long', label: 'July 30, 2026' },
  { value: 'medium', label: 'Jul 30, 2026' },
  { value: 'numeric', label: '07/30/2026' },
  { value: 'short', label: '7/30/26' },
  { value: 'iso', label: '2026-07-30' }
]

// Re-exported so a chart editor can pull the option list and its default from one place.
export { DATE_FORMAT_DEFAULT }

/**
 * Per-column choice of date format, used by the pivot table's row groupings and the data
 * table's display columns. Callers decide which of their columns get the control — it
 * only means anything for date columns.
 *
 * `block` renders it as a full-width form group, for the pivot table's primary Group By
 * row; the default compact layout sits inline beside a column select.
 */
export const DateFormatOption = ({
  Form,
  inputId,
  dateFormat,
  setDateFormat,
  label = 'Date Format',
  block = false,
  width = '150px'
}) => {
  const select = (
    <Form.Select
      id={inputId}
      size="sm"
      value={dateFormat || DATE_FORMAT_DEFAULT}
      onChange={(e) => setDateFormat(e.target.value)}
    >
      {DATE_FORMAT_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Form.Select>
  )

  if (block) {
    return (
      <Form.Group className="mb-3">
        <Form.Label htmlFor={inputId}>{label}</Form.Label>
        {select}
      </Form.Group>
    )
  }

  return (
    <div style={{ width }}>
      <Form.Label className="small mb-1" htmlFor={inputId}>
        {label}
      </Form.Label>
      {select}
    </div>
  )
}

// Aggregations that always produce a whole count of records, so decimals are meaningless
// for them no matter what the underlying column holds.
export const WHOLE_NUMBER_AGGREGATIONS = new Set(['count', 'distinct_count'])

// Display types the number formatter renders with a currency symbol, so the format
// examples below carry one too.
const MONEY_DISPLAY_TYPES = new Set(['money', 'two_digit_money'])

/**
 * Per-column choice between rounding to a whole number and showing two decimal places.
 * Used by both the pivot table's Column Aggregations editor and the data table's
 * Display Columns editor. Callers decide which of their columns get the control — the two
 * tables route different display types through the number formatter.
 *
 * The options are worked examples of the same value rather than descriptions: seeing
 * "1,234.56" next to "1,235" says what the cell will look like more directly than
 * "2 decimals" next to "Whole number" does.
 */
export const DecimalPlacesOption = ({
  Form,
  inputId,
  decimalPlaces,
  setDecimalPlaces,
  displayAs = null,
  width = '150px'
}) => {
  const prefix = MONEY_DISPLAY_TYPES.has(displayAs) ? '$' : ''

  return (
    <div style={{ width }}>
      <Form.Label className="small mb-1" htmlFor={inputId}>
        Format
      </Form.Label>
      <Form.Select
        id={inputId}
        size="sm"
        value={decimalPlaces === 2 ? 2 : 0}
        onChange={(e) => setDecimalPlaces(Number(e.target.value))}
      >
        <option value={0}>{`${prefix}1,235`}</option>
        <option value={2}>{`${prefix}1,234.56`}</option>
      </Form.Select>
    </div>
  )
}

export const YAxisColumnOption = ({
  Form,
  yAxisColumn,
  setYAxisColumn,
  yAxisAggregation,
  setYAxisAggregation,
  validationErrors,
  setValidationErrors,
  hasSubmitted,
  getValueColumns,
  getAggregationOptions,
  getColumnDisplayName,
  municipality
}) => {
  const isInvalid = hasSubmitted && !!validationErrors.yAxisColumn
  return (
    <Form.Group className="mb-3">
      <Form.Label>Y-Axis Column</Form.Label>
      <SearchableColumnSelect
        inputId="y-axis-column-select"
        columns={getValueColumns()}
        value={yAxisColumn}
        onChange={(newColumnId) => {
          setYAxisColumn(newColumnId)
          if (validationErrors.yAxisColumn) {
            setValidationErrors((prev) => ({
              ...prev,
              yAxisColumn: undefined
            }))
          }
          const availableOptions = getAggregationOptions(newColumnId)
          if (!availableOptions.includes(yAxisAggregation)) {
            setYAxisAggregation(availableOptions[0])
          }
        }}
        getColumnDisplayName={getColumnDisplayName}
        municipality={municipality}
        isInvalid={isInvalid}
      />
      {isInvalid && (
        <div className="invalid-feedback d-block">
          {validationErrors.yAxisColumn}
        </div>
      )}
    </Form.Group>
  )
}

export const YAxisAggregationOption = ({
  Form,
  yAxisAggregation,
  setYAxisAggregation,
  yAxisColumn,
  getAggregationOptions,
  getAggregationLabel
}) => (
  <Form.Group className="mb-3">
    <Form.Label>Y-Axis Aggregation</Form.Label>
    <Form.Select
      size="sm"
      value={yAxisAggregation}
      onChange={(e) => setYAxisAggregation(e.target.value)}
    >
      {getAggregationOptions(yAxisColumn).map((option) => (
        <option key={option} value={option}>
          {getAggregationLabel(option)}
        </option>
      ))}
    </Form.Select>
  </Form.Group>
)

export const YAxisLabelOption = ({
  Form,
  yAxisLabel,
  setYAxisLabel
}) => (
  <Form.Group className="mb-3">
    <Form.Label>Y-Axis Label (optional)</Form.Label>
    <Form.Control
      size="sm"
      type="text"
      value={yAxisLabel}
      onChange={(e) => setYAxisLabel(e.target.value)}
      placeholder="e.g., Total Amount, Count of Transactions"
    />
  </Form.Group>
)

export const DisplayLimitOption = ({
  Form,
  displayLimit,
  setDisplayLimit,
  defaultLimit = 10,
  maxLimit = 20
}) => (
  <Form.Group className="mb-3">
    <Form.Label>Limit (number of bars)</Form.Label>
    <Form.Control
      size="sm"
      type="number"
      min={1}
      max={maxLimit}
      value={displayLimit ?? ''}
      placeholder={`${defaultLimit}`}
      onChange={(e) => {
        const raw = e.target.value
        if (raw === '') {
          setDisplayLimit('')
          return
        }
        const parsed = parseInt(raw, 10)
        if (Number.isNaN(parsed)) return
        setDisplayLimit(Math.min(Math.max(parsed, 1), maxLimit))
      }}
    />
    <Form.Text className="text-muted">
      Max bars to render. Defaults to {defaultLimit}; capped at {maxLimit}.
    </Form.Text>
  </Form.Group>
)

export const YAxisGroupByOption = ({
  Form,
  yAxisGroupBy,
  setYAxisGroupBy,
  getGroupingColumns,
  getColumnDisplayName,
  municipality
}) => (
  <Form.Group className="mb-0">
    <Form.Label>Y-Axis Group By (optional)</Form.Label>
    <SearchableColumnSelect
      inputId="y-axis-group-by-select"
      columns={getGroupingColumns()}
      value={yAxisGroupBy}
      onChange={(val) => setYAxisGroupBy(val)}
      getColumnDisplayName={getColumnDisplayName}
      municipality={municipality}
      placeholder="None (show sum line only)"
      isClearable
    />
    <Form.Text className="text-muted">
      Group data by reference, select, or multiselect columns. Shows
      individual group lines plus total sum.
    </Form.Text>
  </Form.Group>
)

export const ShowTotalsOption = ({
  Form,
  showTotals,
  setShowTotals
}) => (
  <Form.Group className="mb-3">
    <Form.Check
      type="switch"
      id="show-totals-switch"
      label="Show totals"
      checked={!!showTotals}
      onChange={(e) => setShowTotals(e.target.checked)}
    />
    <Form.Text className="text-muted">
      Display the total sum line across all groups.
    </Form.Text>
  </Form.Group>
)

export const GroupByOption = ({
  Form,
  groupBy,
  setGroupBy,
  validationErrors,
  setValidationErrors,
  hasSubmitted,
  getGroupingColumns,
  getColumnDisplayName,
  municipality,
  label = 'Group By (Slice Categories)'
}) => {
  const isInvalid = hasSubmitted && !!validationErrors.groupBy
  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <SearchableColumnSelect
        inputId="group-by-select"
        columns={getGroupingColumns()}
        value={groupBy}
        onChange={(val) => {
          setGroupBy(val)
          if (validationErrors.groupBy) {
            setValidationErrors((prev) => ({
              ...prev,
              groupBy: undefined
            }))
          }
        }}
        getColumnDisplayName={getColumnDisplayName}
        municipality={municipality}
        isInvalid={isInvalid}
      />
      {isInvalid && (
        <div className="invalid-feedback d-block">
          {validationErrors.groupBy}
        </div>
      )}
    </Form.Group>
  )
}

export const ValueColumnOption = ({
  Form,
  valueColumn,
  setValueColumn,
  aggregationMethod,
  setAggregationMethod,
  validationErrors,
  setValidationErrors,
  hasSubmitted,
  getValueColumns,
  getAggregationOptions,
  getColumnDisplayName,
  municipality,
  label = 'Slice Column (What Each Slice Represents)',
  allowNone = false
}) => {
  const isInvalid = hasSubmitted && !!validationErrors.valueColumn
  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <SearchableColumnSelect
        inputId="value-column-select"
        columns={getValueColumns()}
        value={valueColumn}
        onChange={(newColumnId) => {
          setValueColumn(newColumnId)
          if (validationErrors.valueColumn) {
            setValidationErrors((prev) => ({
              ...prev,
              valueColumn: undefined
            }))
          }
          const availableOptions = getAggregationOptions(newColumnId)
          if (!availableOptions.includes(aggregationMethod)) {
            setAggregationMethod(availableOptions[0])
          }
        }}
        getColumnDisplayName={getColumnDisplayName}
        municipality={municipality}
        placeholder={allowNone ? 'None (count only)' : 'Select column...'}
        isClearable={allowNone}
        isInvalid={isInvalid}
      />
      {isInvalid && (
        <div className="invalid-feedback d-block">
          {validationErrors.valueColumn}
        </div>
      )}
    </Form.Group>
  )
}

export const AggregationMethodOption = ({
  Form,
  aggregationMethod,
  setAggregationMethod,
  valueColumn,
  getAggregationOptions,
  getAggregationLabel,
  label = 'Slice Aggregation'
}) => {
  if (!valueColumn) return null

  return (
    <Form.Group className="mb-0">
      <Form.Label>{label}</Form.Label>
      <Form.Select
        size="sm"
        value={aggregationMethod}
        onChange={(e) => setAggregationMethod(e.target.value)}
      >
        {getAggregationOptions(valueColumn).map((option) => (
          <option key={option} value={option}>
            {getAggregationLabel(option)}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  )
}

export const KpiValueColumnOption = ({
  Form,
  kpiValueColumn,
  setKpiValueColumn,
  kpiAggregation,
  setKpiAggregation,
  validationErrors,
  setValidationErrors,
  hasSubmitted,
  getValueColumns,
  getAggregationOptions,
  getColumnDisplayName,
  municipality
}) => {
  const isInvalid = hasSubmitted && !!validationErrors.kpiValueColumn
  return (
    <Form.Group className="mb-3">
      <Form.Label>Value Column</Form.Label>
      <SearchableColumnSelect
        inputId="kpi-value-column-select"
        columns={getValueColumns()}
        value={kpiValueColumn}
        onChange={(newColumnId) => {
          setKpiValueColumn(newColumnId)
          if (validationErrors.kpiValueColumn) {
            setValidationErrors((prev) => ({
              ...prev,
              kpiValueColumn: undefined
            }))
          }
          const availableOptions = getAggregationOptions(newColumnId)
          if (!availableOptions.includes(kpiAggregation)) {
            setKpiAggregation(availableOptions[0])
          }
        }}
        getColumnDisplayName={getColumnDisplayName}
        municipality={municipality}
        isInvalid={isInvalid}
      />
      {isInvalid && validationErrors.kpiValueColumn && (
        <div className="invalid-feedback d-block">
          {validationErrors.kpiValueColumn}
        </div>
      )}
    </Form.Group>
  )
}

export const KpiAggregationOption = ({
  Form,
  kpiAggregation,
  setKpiAggregation,
  kpiValueColumn,
  getAggregationOptions,
  getAggregationLabel
}) => (
  <Form.Group className="mb-3">
    <Form.Label>Aggregation</Form.Label>
    <Form.Select
      size="sm"
      value={kpiAggregation}
      onChange={(e) => setKpiAggregation(e.target.value)}
    >
      {getAggregationOptions(kpiValueColumn).map((agg) => (
        <option key={agg} value={agg}>
          {getAggregationLabel(agg)}
        </option>
      ))}
    </Form.Select>
  </Form.Group>
)

export const KpiAnalyticOption = ({
  Form,
  kpiAnalytic,
  setKpiAnalytic,
  analytics,
  sectionId
}) => {
  // Extract array from paginated response and filter analytics by section
  const analyticsArray = analytics?.data || analytics || []
  const sectionAnalytics = Array.isArray(analyticsArray)
    ? analyticsArray.filter(a => a.section_id === sectionId)
    : []

  if (sectionAnalytics.length === 0) return null

  return (
    <Form.Group className="mb-0">
      <Form.Label>Filter by Analytic (optional)</Form.Label>
      <Form.Select
        size="sm"
        value={kpiAnalytic}
        onChange={(e) => setKpiAnalytic(e.target.value)}
      >
        <option value="">All Transactions</option>
        <option value="none">None (No Analytics)</option>
        {sectionAnalytics.map((analytic) => (
          <option key={analytic.id} value={analytic.id}>
            {analytic.name}
          </option>
        ))}
      </Form.Select>
      <Form.Text className="text-muted">
        Filter transactions by analytic: specific analytic, no analytics, or all
      </Form.Text>
    </Form.Group>
  )
}
